import { Component, ChangeDetectionStrategy, computed, signal, inject } from '@angular/core';
import { DateTime } from 'luxon';
import { filter, map, switchMap } from 'rxjs/operators';
import { IPackage } from '../../../../types';
import { ProfileService } from '../../profile.service';
import { DatePipe, NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { PaintDirective } from '../../../../paint.directive';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ConfirmPopup } from '../../../../confirm.popup';

@Component({
  selector: 'subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, DatePipe, NgClass, MatButtonModule, PaintDirective]
})
export class SubscriptionComponent {
  service = inject(ProfileService);
  
  confirm = inject(ConfirmPopup);

  otherPlans = signal<boolean>(false);
  cards = computed(() => this.service.membership()?.cards ?? 0);
  next_package = computed(() => this.service.membership()?.next_package ?? this.package());
  next_package_slug = computed(() => this.next_package()?.slug);
  next_price = computed(() => (this.next_package() || this.package() || { price: 'Unknown' }).price);

  next_plan_text = toSignal(toObservable(this.service.profile).pipe(filter(c => !!c && !!this.next_package_slug()), switchMap(r => this.service.getCoupons(this.next_package_slug(), true)), map(({ uses }) => {
    const next_package = this.next_package();
    ;

    if (!next_package) {
      return '';
    }

    const prefix = next_package._id == this.package()._id ? 'Next renewal' : 'Next plan';

    if (this.service.membership()?.next_transaction) {
      return `${prefix} has been prepaid`;
    }

    const available = uses.filter(c => !c.used && (!c.expiry || (DateTime.fromISO(c.expiry) > DateTime.fromISO(this.service.membership()?.expiry!))));

    if(available.length){
      const days = this.calculateInterval(next_package);
  
      const sortedUses = available.map(use => ({
        _id: use._id,
        price: this.calculatePackagePrice(next_package, use.coupon && use.coupon.percentage, use.coupon && use.coupon.reduction),
        days: use && use.coupon && use.coupon.free_days || 0
      })).sort((a, b) => {
        if (Number(a.price) == Number(b.price) && (a.days > days || b.days > days)) {
          return a.days > b.days ? -1 : 1;
        }
  
        return Number(a.price) > Number(b.price) ? 1 : -1;
      });
  
      const next_coupon = available.find(c => c._id == sortedUses[0]._id);
      if (next_coupon) {
        const next_days = next_coupon.coupon.free_days;
        const next_price = this.calculatePackagePrice(next_package, next_coupon.coupon.percentage, next_coupon.coupon.reduction);
        return `${prefix} will be billed at €${next_price || '???'}` + (next_days ? ` (${next_days} days)` : '');
      }

    }

    return `${prefix} will be billed at €${next_package.price}`;
  })));


  constructor() {
    
  }


  package = computed(() => this.service.membership()?.package || { _id: '', name: 'cloudigo Basic Plan', slug: 'free', plans: ['global', 'basic', 'free'], view_plans: ['free', 'global', 'basic', 'premium', 'extra'], price: '0.00', package_type: 'internal', short_name: 'cloudigo Basic'  } as IPackage);

  package_slug = computed(() => {
    const pkg = this.package();
    if(pkg.package_type == 'corporate'){
      return pkg.tags?.includes('tourist') ? 'tourist' : 'corporate';
    }
    return pkg?.slug;
  });

  package_tags = computed(() => {
    const pkg = this.package();

    return pkg?.tags || [];
  })

  calculateInterval(pkg: IPackage) {
    if (!pkg.unit) return 0;
    const today = DateTime.now();//declaring this and reusing it in expiry is required to ensure the difference calculated is not off by milliseconds
    const expiry = today.plus({ [pkg.unit]: pkg.duration });
    return expiry.diff(today).as('days');
  }

  calculatePackagePrice(pkg: IPackage, isPercentage: boolean, discount: number) {
    if (!pkg) return -1;

    let price = Number(pkg.price);

    if (isPercentage) {
      price = price * (1 - (discount / 100));
    } else {
      price -= discount;
    }

    price = Math.max(0, Math.min(price, Number(pkg.price)));

    return price.toFixed(2);
  }

  toggleRenewal(state: boolean = false){

    const sub = this.service.membership();

    if(!sub?.expiry){
      //!Membership does not expire, so no point in toggling
      return;
    }

    if(state && !this.cards()){
      //TODO: prompt add card?
      // this.ss.trackPremiumClick('Profile Panel', 'Resubscribe');
      this.confirm.open('Add a card', 'A payment method associated with your account is required. Would you like to add one?', 'Requires a payment method Prompt', [
        {
          label: 'No',
          paint: 'warn',
          close: true
        },
        {
          label: 'Yes',
          paint: 'primary',
          close: true,
          route: '/profile/billing'
        }
      ]);
      return;
    }


    const popup = state ? {
      title: 'Renew Subscription?',
      message: `Are you sure you would like <b>renew</b> your subscription?<br/><br/> By renewing your subscription, you will start being billed again ${sub?.next_transaction ? ' when your prepaid subscription period ends' : ('on <b>'+ DateTime.fromISO(sub?.expiry).toFormat('d MMMM, yyyy') +'</b>')}`,
      success_title: 'Your Subscription has been renewed.',
      event_category: 'Renewed Subscription'
    } : {
      title: 'Cancel Subscription?',
      message: `Are you sure you would like <b>cancel</b> your subscription?<br/><br/> By cancelling your subscription, you will be reverted back to the free plan ${sub?.next_transaction ? ' when your prepaid subscription period ends' : ('on <b>'+ DateTime.fromISO(sub?.expiry).toFormat('d MMMM, yyyy') +'</b>')}`,
      success_title: 'Your Subscription has been cancelled.',
      event_category: 'Cancelled Subscription'
    };

    this.confirm.open(popup.title, popup.message, popup.title.replace('?', '') + ' Prompt', [
      {
        label: 'No',
        paint: 'warn',
        close: true
      },
      {
        label: 'Yes',
        paint: 'primary',
        close: true,
        callback: () => {
          this.service.toggleSubscription(sub._id, state).subscribe({
            next: result => {
              this.service.profile.update(profile => {

                if(profile?.membership){
                  profile.membership.recurs = state;
                  profile.membership.next_package = undefined;
                }

                return Object.assign({}, profile);
              });

              this.service.toast.success(popup.success_title);
            },
            error: error => {
              error = error.error||error;
              this.service.toast.error(error.message);
            }
          });
        }
      }
    ]);
  }
}
